/**
 * RunnerSet —— 多套件并行容器。
 *
 * 单个 logcat tab / CLI run-once 拥有一个 RunnerSet：
 *   - 同一行只解析 + 升序编号一次，便于 UI 行内 marker / hit 反查；
 *   - 套件之间相互独立，feed 时把行广播给所有 runner；
 *   - hitsAt(lineId) 遍历所有 runner 合并结果。
 */

import { SuiteRunner, type SuiteEventListener, type SuiteRunnerOptions } from './engine';
import type { Hit, LogcatLineView, SuiteSnapshot } from './types';
import { applyParams } from './params';
import type { Suite } from './types';

export interface RunnerSetAddOptions {
  runId: string;
  suite: Suite;
  /** UI 收集到的参数；缺省 = 直接 applyParams 一个空对象（套件未声明参数时）。 */
  params?: Record<string, string | number | boolean>;
  /** 注入起始时间，离线扫描时可设置成文件首行 tsMs。 */
  startedAt?: number;
}

export class RunnerSet {
  private readonly runners = new Map<string, SuiteRunner>();
  private readonly listeners = new Set<SuiteEventListener>();
  /** 暂停集合：feed 时这些 runner 不接收当前行（保留状态与 verdict）。 */
  private readonly paused = new Set<string>();
  /** 行计数器；caller 可不传 lineId 让 RunnerSet 自动分配。 */
  private nextLineId = 0;

  on(listener: SuiteEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** 装载一个套件。重复 runId 抛错，方便 UI 早发现。 */
  add(opts: RunnerSetAddOptions): SuiteRunner {
    if (this.runners.has(opts.runId)) {
      throw new Error(`RunnerSet: duplicate runId ${opts.runId}`);
    }
    const applied = applyParams(opts.suite, opts.params ?? {});
    const runnerOpts: SuiteRunnerOptions = {
      runId: opts.runId,
      suite: applied,
      params: opts.params,
      startedAt: opts.startedAt,
    };
    const runner = new SuiteRunner(runnerOpts);
    runner.on((ev) => {
      for (const l of this.listeners) {
        try {
          l(ev);
        } catch {
          /* noop */
        }
      }
    });
    this.runners.set(opts.runId, runner);
    return runner;
  }

  /** 卸载一个套件；若它仍 running，调用 `finish()` 并 emit finished。 */
  remove(runId: string): void {
    const r = this.runners.get(runId);
    if (!r) return;
    if (!r.isFinished()) r.finish();
    this.runners.delete(runId);
    this.paused.delete(runId);
  }

  has(runId: string): boolean {
    return this.runners.has(runId);
  }

  list(): SuiteRunner[] {
    return Array.from(this.runners.values());
  }

  /**
   * 喂入一行。如果 line.lineId === undefined（或调用方不在乎），
   * RunnerSet 会自动 nextLineId++ 并写回；否则尊重传入 id（用于离线
   * 扫描已有编号的场景）。
   */
  feed(line: LogcatLineView | Omit<LogcatLineView, 'lineId'>): LogcatLineView {
    const withId: LogcatLineView =
      'lineId' in line && typeof (line as LogcatLineView).lineId === 'number'
        ? (line as LogcatLineView)
        : ({ ...(line as Omit<LogcatLineView, 'lineId'>), lineId: this.nextLineId++ } as LogcatLineView);
    if (withId.lineId >= this.nextLineId) this.nextLineId = withId.lineId + 1;
    for (const [id, r] of this.runners.entries()) {
      if (this.paused.has(id)) continue;
      r.feed(withId);
    }
    return withId;
  }

  /** 暂停某个 runner（保留 verdict / hits，仅停止接收新行）。 */
  pause(runId: string): void {
    if (this.runners.has(runId)) this.paused.add(runId);
  }

  /** 恢复 feed。 */
  resume(runId: string): void {
    this.paused.delete(runId);
  }

  isPaused(runId: string): boolean {
    return this.paused.has(runId);
  }

  /** 给所有 runner 推一次时钟，触发可能的 timeout。 */
  tick(nowMs: number): void {
    for (const r of this.runners.values()) r.tick(nowMs);
  }

  /** 收尾所有 runner（离线扫描完成时调用）。 */
  finishAll(nowMs?: number): void {
    for (const r of this.runners.values()) r.finish(nowMs);
  }

  /** 获取所有 runner 的当前快照。 */
  snapshot(): SuiteSnapshot[] {
    return Array.from(this.runners.values(), (r) => r.snapshot());
  }

  /** 反查某一行命中的所有规则（跨套件）。 */
  hitsAt(lineId: number): Hit[] {
    const out: Hit[] = [];
    for (const r of this.runners.values()) {
      const arr = r.hitsAt(lineId);
      if (arr.length > 0) out.push(...arr);
    }
    return out;
  }

  /** 当前已分配到的 lineId 上界（下一个待分配的）。 */
  getNextLineId(): number {
    return this.nextLineId;
  }
}
